import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import log from 'sentry/log'
import { Snackbar, IconWarning } from '@blocket/blocket-ui'
import { actionCreators as api } from 'reduxBits/api'
import ReportUserModal from 'components/sharedComponents/Modals/ReportUserModal'
import { getErrorMessageCode } from 'utils/errorMessageCode'
import {
  trackReportUserModalView,
  trackReportUserModalThankYouView,
  trackReportUserModalInputErrorView,
  trackReportUserCustomerSupport,
  trackSubmitReportUser,
} from 'tracking/profilePagePublic'
import { validateInput } from './validateInput'

const ErrorBarWrapper = styled.div`
  position: fixed;
  z-index: 100;
  left: 0px;
  top: 62px;
  width: 100%;
  ${(props) => !props.active && 'display: none'}
`

const ReportUser = ({ closeReportUserModal, account }) => {
  const [reportUserReasons, setReportUserReasons] = useState([])
  const [selectedUserReason, setSelectedtUserReason] = useState({})
  const [reportUserInputValue, setReportUserInputValue] = useState('')
  const [formValidation, setFormValidation] = useState({})
  const [renderReportUserPage, setRenderReportUserPage] = useState('form')
  const [generalError, setGeneralError] = useState({})
  const [showErrorBar, setShowErrorBar] = useState(false)

  const dispatch = useDispatch()

  const reasonsExists = Boolean(reportUserReasons.length)

  const handleGeneralErrors = (error, clientErrorId) => {
    const errorMessage = getErrorMessageCode(error, 'REU', clientErrorId)
    setGeneralError(errorMessage)
    log.message('Error: report user: report-user/v1/reasons', error)
    return errorMessage
  }

  useEffect(() => {
    let timer
    if (showErrorBar) {
      timer = setTimeout(() => {
        closeReportUserModal()
      }, 5000)
    }

    return () => {
      clearTimeout(timer)
    }
  }, [showErrorBar, closeReportUserModal])

  useEffect(() => {
    if (!reasonsExists && !showErrorBar) {
      const fetchData = async () => {
        try {
          const response = await dispatch(api.fetch('report-user/v1/reasons?domain=profile'))
          setReportUserReasons(response.reasons)
        } catch (error) {
          handleGeneralErrors(error, '001')
          setShowErrorBar(true)
        }
      }
      fetchData()
    }
  }, [reasonsExists, showErrorBar, dispatch])

  const postData = async () => {
    const data = {
      reason: selectedUserReason.type,
      reasonDescription: reportUserInputValue,
      domain: 'profile',
      domainRef: account.blocketAccountId,
      reportedBlocketAccountId: account.blocketAccountId,
    }
    try {
      const response = await dispatch(
        api.fetch('report-user/v1/report', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      )
      if (response.ok) {
        setRenderReportUserPage('sent')
      }
    } catch (error) {
      const errorMessage = handleGeneralErrors(error, '002')
      trackReportUserModalInputErrorView(errorMessage)
    }
  }

  const submitReportUser = (event) => {
    event.preventDefault()
    const { requiresFreeText } = selectedUserReason
    trackSubmitReportUser()

    const { hasError, errorText } = validateInput(reportUserInputValue, requiresFreeText)
    if (hasError) {
      setFormValidation({ status: 'error', text: errorText })
      trackReportUserModalInputErrorView(errorText)
      return
    }
    postData()
  }

  const setUserInputValue = (value) => {
    setReportUserInputValue(value)
    if (!value.length > 0) {
      setFormValidation({})
    } else {
      setFormValidation({ status: 'success', text: '' })
    }
  }

  const reportUserReasonClick = (reason) => {
    setSelectedtUserReason(reason)
  }

  return (
    <>
      {showErrorBar && (
        <ErrorBarWrapper active={showErrorBar}>
          <Snackbar
            active={showErrorBar}
            type="error"
            description={generalError.errorCode}
            subject={generalError.message}
            icon={<IconWarning width={24} height={24} color="#fff" />}
            showCloseBtn={false}
          />
        </ErrorBarWrapper>
      )}
      {!showErrorBar && reasonsExists && (
        <ReportUserModal
          onClose={closeReportUserModal}
          open={true}
          options={reportUserReasons}
          onPillClick={reportUserReasonClick}
          onChangeInput={setUserInputValue}
          onSubmitForm={submitReportUser}
          formValidation={formValidation}
          renderPage={renderReportUserPage}
          reportUserPage="Profile"
          generalError={generalError}
          viewTracking={
            renderReportUserPage === 'form'
              ? trackReportUserModalView
              : trackReportUserModalThankYouView
          }
          onLinkClick={trackReportUserCustomerSupport}
        />
      )}
    </>
  )
}

ReportUser.propTypes = {
  closeReportUserModal: PropTypes.func.isRequired,
  account: PropTypes.object.isRequired,
}

export default ReportUser
